import { useMutation } from "@tanstack/react-query";
import { useState } from "react";
import { Link, useLocation } from "react-router-dom";

import { api } from "../api";
import { useSession } from "../session";
import { Alert, Field } from "../shell/ui";

/**
 * The feedback button on every page: a bug, an idea, or praise, sent with the
 * page it came from so "it broke" arrives with where. Only the people running
 * the instance read it.
 */
export function FeedbackDialog({ onClose }: { onClose: () => void }) {
  const { user } = useSession();
  const { pathname } = useLocation();
  const [kind, setKind] = useState<"bug" | "idea" | "feedback">("idea");
  const [text, setText] = useState("");
  const send = useMutation({
    mutationFn: () =>
      api<{ id: number }>("/feedback", {
        method: "POST",
        body: { kind, message: text, route: pathname },
      }),
  });

  return (
    <div className="dialog-backdrop" role="presentation" onClick={onClose}>
      <div
        className="dialog card"
        role="dialog"
        aria-modal="true"
        aria-labelledby="fb-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="card__body stack">
          <h2 id="fb-title">Send feedback</h2>
          {!user ? (
            <p>
              <Link to="/signin" onClick={onClose}>
                Sign in
              </Link>{" "}
              to send feedback.
            </p>
          ) : send.data ? (
            <>
              <Alert tone="ok">Thank you, it's sent.</Alert>
              <div className="row row--end">
                <button type="button" className="btn btn--primary" onClick={onClose}>
                  Close
                </button>
              </div>
            </>
          ) : (
            <form
              className="stack"
              onSubmit={(e) => {
                e.preventDefault();
                send.mutate();
              }}
            >
              <div className="segmented" role="radiogroup" aria-label="Kind of feedback">
                {(
                  [
                    ["bug", "🐞 Something broke"],
                    ["idea", "💡 An idea"],
                    ["feedback", "♥ Other"],
                  ] as const
                ).map(([k, l]) => (
                  <button
                    key={k}
                    type="button"
                    role="radio"
                    aria-checked={kind === k}
                    className={kind === k ? "is-active" : undefined}
                    onClick={() => setKind(k)}
                  >
                    {l}
                  </button>
                ))}
              </div>
              <Field
                label={
                  kind === "bug"
                    ? "What happened? What did you expect?"
                    : kind === "idea"
                      ? "What would you like, and why?"
                      : "What do you want to tell us?"
                }
              >
                <textarea
                  rows={5}
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  maxLength={5000}
                  required
                  autoFocus
                />
              </Field>
              <p className="muted small">Sent with: this page ({pathname}), your layout, and your browser.</p>
              {send.error && <Alert tone="error">{send.error.message}</Alert>}
              <div className="row row--end">
                <button type="button" className="btn" onClick={onClose}>
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn--primary"
                  disabled={send.isPending || text.trim().length < 3}
                >
                  Send
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
